import { Chart as ChartJS } from 'chart.js'
import { MouseEvent as ReactMouseEvent, RefObject, useCallback, useMemo, useState } from 'react'

import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import type { SecondData } from '../../types'
import {
  MIN_INTENSITY,
  ROW_H,
  ROW_TOP_PAD,
  TOOLTIP_FOOTER_HEIGHT,
  TOOLTIP_GAP,
  TOOLTIP_PADDING_PX,
  TOOLTIP_WIDTH,
  colorForRow,
  snapToSecond,
} from './TimelinePalette'

export interface TooltipItem {
  label: string
  intensity: number
  color: string
}

export interface TooltipState {
  visible: boolean
  second: number
  anchorX: number
  anchorY: number
  /**
   * Viewport y-coordinate of the chart's first motivation row (top edge).
   * Captured at hover-time so the tooltip can pin its rows block at the same
   * y as the chart rows for row-by-row visual alignment.
   */
  chartRowsTop: number
  items: TooltipItem[]
}

export interface TooltipPosition {
  left: number
  top: number
}

/**
 * Build the items list (in `motivationKeysInOrder`) for a single second.
 * Every motivation row is returned so the tooltip mirrors the timeline's full
 * row stack — entries at the floor (intensity 1) read as "no signal here".
 */
const buildTooltipItems = (secondData: SecondData, motivationKeysInOrder: string[], motivationLabel: (key: string) => string): TooltipItem[] =>
  motivationKeysInOrder.map((key, rowIndex) => ({
    label: motivationLabel(key),
    // Floor to MIN_INTENSITY (1.0) so missing / zero data points read as the
    // baseline "no signal" value rather than "0.0", matching how the chart
    // rows treat intensity 1 as the row floor.
    intensity: Math.max(secondData.intensity[key] ?? MIN_INTENSITY, MIN_INTENSITY),
    color: colorForRow(rowIndex),
  }))

interface UseTimelineTooltipArgs {
  seconds: SecondData[]
  numRows: number
  chartRef: RefObject<ChartJS<'line'>>
  onSecondClick?: (second: number) => void
  motivationKeysInOrder: string[]
}

interface UseTimelineTooltipResult {
  tooltip: TooltipState | null
  tooltipPosition: TooltipPosition | null
  hoverSecond: number | null
  handleWrapperMouseMove: (event: ReactMouseEvent<HTMLDivElement>) => void
  handleWrapperClick: (event: ReactMouseEvent<HTMLDivElement>) => void
  handleWrapperMouseLeave: () => void
}

/**
 * Hover/click + tooltip state machine for {@link MotivationTimeline}.
 *
 * - Resolves pointer events to a snapped `second` using the live Chart.js
 *   x-scale (handles axis ticks, scrolling, canvas DPI for free).
 * - Tracks `hoverSecond` so the chart's annotation cursor can highlight the
 *   pointed-at column.
 * - Builds a viewport-anchored {@link TooltipState} on each move so the
 *   tooltip portal can be rendered with `position: fixed` (escapes the
 *   horizontal scroll container's overflow clipping).
 * - Suppresses hover visuals when the cursor is over the x-axis label strip
 *   so they don't fight the chart cursor's vertical extent.
 */
export const useTimelineTooltip = ({ seconds, numRows, chartRef, onSecondClick, motivationKeysInOrder }: UseTimelineTooltipArgs): UseTimelineTooltipResult => {
  const { motivationLabel } = useUATaxonomy()
  const [hoverSecond, setHoverSecond] = useState<number | null>(null)
  const [tooltip, setTooltip] = useState<TooltipState | null>(null)

  const eventToSecond = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>): number | null => {
      const chart = chartRef.current
      if (!chart) return null
      const canvasRect = chart.canvas.getBoundingClientRect()
      const canvasX = event.clientX - canvasRect.left
      const rawX = chart.scales.x.getValueForPixel(canvasX)
      if (rawX == null) return null
      return snapToSecond(rawX, seconds)
    },
    [chartRef, seconds]
  )

  const handleWrapperMouseMove = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      const chart = chartRef.current
      if (!chart) return
      const canvasRect = chart.canvas.getBoundingClientRect()
      const cursorY = event.clientY - canvasRect.top
      const plotBottom = ROW_TOP_PAD + numRows * ROW_H
      if (cursorY > plotBottom) {
        setHoverSecond(null)
        setTooltip(null)
        // Keep the pointer cursor over the x-axis label strip so the user
        // gets a "this is clickable" affordance even though hover visuals
        // are suppressed there.
        if (onSecondClick && chart.canvas) chart.canvas.style.cursor = 'pointer'
        return
      }
      const second = eventToSecond(event)
      if (second == null) return
      setHoverSecond(second)
      const secondData = seconds.find((s) => s.second === second)
      if (!secondData) return
      // Anchor is stored in *viewport* coordinates so the tooltip can be
      // portal-rendered with `position: fixed`.
      const snappedPx = chart.scales.x.getPixelForValue(second)
      const anchorX = canvasRect.left + (snappedPx ?? event.clientX - canvasRect.left)
      const plotTopViewport = canvasRect.top + ROW_TOP_PAD
      const plotBottomViewport = canvasRect.top + plotBottom
      const anchorY = Math.max(plotTopViewport, Math.min(plotBottomViewport, event.clientY))
      setTooltip({
        visible: true,
        second,
        anchorX,
        anchorY,
        chartRowsTop: plotTopViewport,
        items: buildTooltipItems(secondData, motivationKeysInOrder, motivationLabel),
      })
      if (onSecondClick && chart.canvas) chart.canvas.style.cursor = 'pointer'
    },
    [chartRef, eventToSecond, seconds, numRows, onSecondClick, motivationKeysInOrder, motivationLabel]
  )

  const handleWrapperClick = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (!onSecondClick) return
      const second = eventToSecond(event)
      if (second != null) onSecondClick(second)
    },
    [eventToSecond, onSecondClick]
  )

  const handleWrapperMouseLeave = useCallback(() => {
    setHoverSecond(null)
    setTooltip(null)
    const canvas = chartRef.current?.canvas
    if (canvas) canvas.style.cursor = 'default'
  }, [chartRef])

  // Tooltip is always pinned to the LEFT of the cursor with a small gap. Left
  // is clamped to the viewport so the tooltip is never clipped off-screen,
  // but it never flips to the right side — by design the tooltip stays on
  // the left so it doesn't cover the data point the user is pointing at.
  const tooltipPosition = useMemo<TooltipPosition | null>(() => {
    if (!tooltip) return null
    const rawLeft = tooltip.anchorX - TOOLTIP_GAP - TOOLTIP_WIDTH
    const left = Math.max(8, rawLeft)
    // Anchor the rows block exactly on top of the chart's first motivation
    // row so each tooltip row sits at the same y as its corresponding chart
    // row. The footer (divider + title) sits below the rows, so only the
    // top padding offsets the rows from the outer tooltip top edge.
    const totalHeight = TOOLTIP_PADDING_PX + tooltip.items.length * ROW_H + TOOLTIP_FOOTER_HEIGHT
    const viewportH = typeof window === 'undefined' ? 800 : window.innerHeight
    const rawTop = tooltip.chartRowsTop - TOOLTIP_PADDING_PX
    const top = Math.min(Math.max(rawTop, 8), Math.max(8, viewportH - totalHeight - 8))
    return { left, top }
  }, [tooltip])

  return { tooltip, tooltipPosition, hoverSecond, handleWrapperMouseMove, handleWrapperClick, handleWrapperMouseLeave }
}
